/**
 * MIME类型标准化工具
 * 将浏览器返回的非标准MIME类型转换为后端接口支持的标准MIME类型
 */

// 根据图片中的MIME_TYPE_MAPPINGS，定义标准MIME类型映射
const MIME_TYPE_MAPPINGS: Record<string, Set<string>> = {
  // 图片类型
  'image/jpeg': new Set(['.jpg', '.jpeg', '.jpe']),
  'image/png': new Set(['.png']),
  'image/gif': new Set(['.gif']),
  'image/bmp': new Set(['.bmp']),
  'image/webp': new Set(['.webp']),
  'image/svg+xml': new Set(['.svg']),
  'image/tiff': new Set(['.tiff', '.tif']),
  'image/x-icon': new Set(['.ico']),
  // ⚠️ 不要在这里加 'image/heic' / 'image/heif'：后端 mime_validator.py 的
  // MIME_TYPE_MAPPINGS 目前不认这两种，前端一旦按扩展名把它们推断成合法 image/*，
  // 客户端校验就会放行、随后 presign 必吃 422——用户传到一半才失败，比在选择时
  // 立刻收到 unsupportedType 更糟。等后端 MIME_TYPE_MAPPINGS 补 heic/heif 再放开
  // （撤回于 2026-08-30 发布前置检查）。第二道防线在
  // src/features/attachments/publicUpload.ts 的 CLIENT_DENIED_MIME_TYPES —— 放开时两处一起改。

  // 文档类型
  'application/pdf': new Set(['.pdf']),
  'application/msword': new Set(['.doc']),
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': new Set(['.docx']),
  'application/vnd.ms-excel': new Set(['.xls']),
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': new Set(['.xlsx']),
  'application/vnd.ms-powerpoint': new Set(['.ppt']),
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': new Set(['.pptx']),

  // 文本类型
  'text/plain': new Set(['.txt']),
  'text/html': new Set(['.html', '.htm']),
  'text/css': new Set(['.css']),
  'text/javascript': new Set(['.js']),
  'application/javascript': new Set(['.js']),
  'text/markdown': new Set(['.md', '.markdown']),
  'text/csv': new Set(['.csv']),
  'application/json': new Set(['.json']),
  'application/xml': new Set(['.xml']),
  'text/xml': new Set(['.xml']),

  // 压缩文件
  'application/zip': new Set(['.zip']),
  'application/x-rar-compressed': new Set(['.rar']),
  'application/x-7z-compressed': new Set(['.7z']),
  'application/x-tar': new Set(['.tar']),
  'application/gzip': new Set(['.gz']),

  // 视频类型
  'video/mp4': new Set(['.mp4']),
  'video/mpeg': new Set(['.mpeg', '.mpg']),
  'video/quicktime': new Set(['.mov']),
  'video/x-msvideo': new Set(['.avi']),
  'video/webm': new Set(['.webm']),

  // 音频类型
  'audio/mpeg': new Set(['.mp3']),
  'audio/wav': new Set(['.wav']),
  'audio/ogg': new Set(['.ogg']),
  'audio/mp4': new Set(['.m4a']),
  'audio/x-m4a': new Set(['.m4a']),
  'audio/webm': new Set(['.weba'])
}

// 浏览器非标准MIME类型到标准MIME类型的映射
const BROWSER_MIME_TYPE_FIXES: Record<string, string> = {
  // 压缩文件常见的非标准类型
  'application/x-zip-compressed': 'application/zip',
  'application/x-zip': 'application/zip',
  'application/zip-compressed': 'application/zip',
  
  // RAR文件
  'application/x-rar': 'application/x-rar-compressed',
  'application/rar': 'application/x-rar-compressed',
  
  // 7z文件
  'application/x-7z': 'application/x-7z-compressed',
  'application/7z': 'application/x-7z-compressed',
  
  // JavaScript文件
  'text/javascript': 'application/javascript',
  
  // 音频文件
  'audio/x-mpeg': 'audio/mpeg',
  'audio/mp3': 'audio/mpeg',
  
  // 视频文件
  'video/x-ms-wmv': 'video/x-msvideo',
  'video/x-ms-asf': 'video/x-msvideo',
  
  // 图片文件
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
  'image/x-png': 'image/png',
  
  // Office文档的旧版本MIME类型
  'application/vnd.ms-word': 'application/msword',
  'application/vnd.ms-excel.sheet.macroEnabled.12': 'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint.presentation.macroEnabled.12': 'application/vnd.ms-powerpoint',
  
  // XML文件
  'text/xml': 'application/xml',
  
  // 其他常见的非标准类型
  'application/octet-stream': '', // 需要通过文件扩展名推断
}

/**
 * 根据文件扩展名获取标准MIME类型
 */
function getMimeTypeByExtension(fileName: string): string | null {
  const extension = '.' + fileName.split('.').pop()?.toLowerCase()
  if (!extension || extension === '.') return null
  
  for (const [mimeType, extensions] of Object.entries(MIME_TYPE_MAPPINGS)) {
    if (extensions.has(extension)) {
      return mimeType
    }
  }
  
  return null
}

/**
 * 标准化MIME类型
 * @param originalMimeType 浏览器返回的原始MIME类型
 * @param fileName 文件名（用于在MIME类型不准确时推断）
 * @returns 标准化后的MIME类型
 */
export function normalizeMimeType(originalMimeType: string, fileName?: string): string {
  // 如果原始MIME类型为空，尝试通过文件扩展名推断
  if (!originalMimeType || originalMimeType === 'application/octet-stream') {
    if (fileName) {
      const inferredType = getMimeTypeByExtension(fileName)
      if (inferredType) {
        return inferredType
      }
    }
    // 如果无法推断，返回通用二进制类型
    return 'application/octet-stream'
  }
  
  // 转换为小写进行比较
  const lowerMimeType = originalMimeType.toLowerCase().trim()
  
  // 检查是否需要修复
  if (BROWSER_MIME_TYPE_FIXES.hasOwnProperty(lowerMimeType)) {
    const fixedType = BROWSER_MIME_TYPE_FIXES[lowerMimeType]
    
    // 如果修复后的类型为空字符串，说明需要通过文件扩展名推断
    if (fixedType === '' && fileName) {
      const inferredType = getMimeTypeByExtension(fileName)
      if (inferredType) {
        return inferredType
      }
    }
    
    return fixedType || originalMimeType
  }
  
  // 检查是否是已知的标准MIME类型
  if (Object.keys(MIME_TYPE_MAPPINGS).includes(lowerMimeType)) {
    return lowerMimeType
  }
  
  // 如果不是标准类型，但有文件名，尝试通过扩展名验证和修正
  if (fileName) {
    const inferredType = getMimeTypeByExtension(fileName)
    if (inferredType) {
      // 如果推断出的类型与原始类型的主类型一致，使用推断的类型
      const originalMainType = lowerMimeType.split('/')[0]
      const inferredMainType = inferredType.split('/')[0]
      
      if (originalMainType === inferredMainType) {
        return inferredType
      }
    }
  }
  
  // 如果无法修正，返回原始类型
  return originalMimeType
}

/**
 * 验证MIME类型是否被后端支持
 * @param mimeType MIME类型
 * @returns 是否支持
 */
export function isSupportedMimeType(mimeType: string): boolean {
  const normalizedType = mimeType.toLowerCase().trim()
  return Object.keys(MIME_TYPE_MAPPINGS).includes(normalizedType)
}

/**
 * 获取文件类型代码（与upload-service.ts中的函数保持一致）
 * @param mimeType MIME类型
 * @returns 文件类型代码
 */
export function getFileTypeCode(mimeType: string, fileName?: string): 'image' | 'audio' | 'pdf' | 'video' | 'document' {
  const normalizedType = normalizeMimeType(mimeType, fileName)
  
  if (normalizedType.startsWith('image/')) return 'image'
  if (normalizedType.startsWith('video/')) return 'video'
  if (normalizedType.startsWith('audio/')) return 'audio'
  if (normalizedType === 'application/pdf') return 'pdf'
  return 'document'
}

/**
 * 获取支持的文件扩展名列表
 * @param fileTypeCode 文件类型代码
 * @returns 支持的扩展名数组
 */
export function getSupportedExtensions(fileTypeCode?: 'image' | 'audio' | 'pdf' | 'video' | 'document'): string[] {
  const extensions: string[] = []
  
  for (const [mimeType, exts] of Object.entries(MIME_TYPE_MAPPINGS)) {
    if (!fileTypeCode) {
      extensions.push(...Array.from(exts))
    } else {
      const typeCode = getFileTypeCode(mimeType)
      if (typeCode === fileTypeCode) {
        extensions.push(...Array.from(exts))
      }
    }
  }
  
  return [...new Set(extensions)].sort()
}